/**
 * Core OT (Operational Transform) pure functions.
 *
 * Implements the classical text OT model:
 *   - Retain(n): positive integer — skip n chars
 *   - Insert(s): string — insert text
 *   - Delete(n): negative integer — delete n chars
 *
 * Key properties maintained:
 *   - TP1: transform(a, b) produces (a', b') where apply(apply(doc, a), b') === apply(apply(doc, b), a')
 *   - Operations are compact (no adjacent same-type components)
 *   - Every operation consumes exactly the input document length
 */

import { OTComponent, OTComponents } from '../types/ot';

// ─── Component helpers ───────────────────────────────────────────────

function isRetain(c: OTComponent): c is number {
  return typeof c === 'number' && c > 0;
}

function isInsert(c: OTComponent): c is string {
  return typeof c === 'string';
}

function isDelete(c: OTComponent): c is number {
  return typeof c === 'number' && c < 0;
}

/** Length consumed from the input document by a single component */
function inputLen(c: OTComponent): number {
  if (isRetain(c)) return c;
  if (isDelete(c)) return -c;
  return 0; // inserts don't consume input
}

/** Length produced in the output document by a single component */
function outputLen(c: OTComponent): number {
  if (isRetain(c)) return c;
  if (isInsert(c)) return c.length;
  return 0; // deletes don't produce output
}

// ─── Compact ─────────────────────────────────────────────────────────

/**
 * Compact an operation by merging adjacent same-type components
 * and removing no-ops (retain 0, empty insert, delete 0).
 */
export function compact(op: OTComponents): OTComponents {
  const result: OTComponents = [];
  for (const c of op) {
    if (isRetain(c) && c === 0) continue;
    if (isInsert(c) && c === '') continue;
    if (isDelete(c) && c === 0) continue;

    if (result.length === 0) {
      result.push(c);
      continue;
    }

    const last = result[result.length - 1];

    if (isRetain(c) && isRetain(last)) {
      result[result.length - 1] = (last as number) + c;
    } else if (isInsert(c) && isInsert(last)) {
      result[result.length - 1] = (last as string) + c;
    } else if (isDelete(c) && isDelete(last)) {
      result[result.length - 1] = (last as number) + c; // both negative, sum
    } else {
      result.push(c);
    }
  }
  // Remove trailing retain (it's implicit)
  while (result.length > 0 && isRetain(result[result.length - 1])) {
    result.pop();
  }
  return result;
}

// ─── Apply ───────────────────────────────────────────────────────────

/**
 * Apply an operation to a document string, producing a new document.
 * Throws if the operation doesn't match the document length.
 */
export function applyOp(doc: string, op: OTComponents): string {
  const parts: string[] = [];
  let pos = 0;

  for (const c of op) {
    if (isRetain(c)) {
      if (pos + c > doc.length) {
        throw new Error(`Retain(${c}) overflows document of length ${doc.length} at position ${pos}`);
      }
      parts.push(doc.slice(pos, pos + c));
      pos += c;
    } else if (isInsert(c)) {
      parts.push(c);
    } else if (isDelete(c)) {
      const deleteCount = -c;
      if (pos + deleteCount > doc.length) {
        throw new Error(`Delete(${deleteCount}) overflows document of length ${doc.length} at position ${pos}`);
      }
      pos += deleteCount;
    }
  }

  // Remaining text is implicitly retained
  if (pos < doc.length) {
    parts.push(doc.slice(pos));
  }

  return parts.join('');
}

/**
 * Calculate the input length an operation expects (sum of retain + |delete|).
 */
export function opInputLength(op: OTComponents): number {
  let len = 0;
  for (const c of op) {
    len += inputLen(c);
  }
  return len;
}

/**
 * Calculate the output length an operation produces.
 */
export function opOutputLength(op: OTComponents): number {
  let total = 0;
  for (const c of op) {
    total += outputLen(c);
  }
  return total;
}

// ─── Iterator for walking through operations component by component ──

interface OpIterator {
  /** Take up to `n` input-consuming units from the current component.
   *  For insert, takes up to `n` characters.
   *  Returns the component piece taken. */
  take(n: number): OTComponent;
  /** Peek at the current component without consuming */
  peek(): OTComponent | null;
  /** Whether there are more components */
  hasNext(): boolean;
}

function makeIterator(op: OTComponents): OpIterator {
  let idx = 0;
  let offset = 0;

  return {
    take(n: number): OTComponent {
      const c = op[idx];
      if (c === undefined) {
        // Beyond end — implicit retain
        return n;
      }

      if (isRetain(c)) {
        const available = c - offset;
        if (n >= available) {
          offset = 0;
          idx++;
          return available;
        } else {
          offset += n;
          return n;
        }
      }

      if (isDelete(c)) {
        const available = (-c) - offset;
        if (n >= available) {
          offset = 0;
          idx++;
          return -available;
        } else {
          offset += n;
          return -n;
        }
      }

      // Insert: take up to n characters
      if (isInsert(c)) {
        const str = c as string;
        const available = str.length - offset;
        if (n >= available) {
          const piece = str.slice(offset);
          offset = 0;
          idx++;
          return piece;
        } else {
          const piece = str.slice(offset, offset + n);
          offset += n;
          return piece;
        }
      }

      return n; // should not reach
    },

    peek(): OTComponent | null {
      const c = op[idx];
      if (c === undefined) return null;

      if (isRetain(c)) return c - offset;
      if (isDelete(c)) return -((-c) - offset);
      if (isInsert(c)) return (c as string).slice(offset);
      return null;
    },

    hasNext(): boolean {
      return idx < op.length;
    },
  };
}

// ─── Transform ───────────────────────────────────────────────────────

/**
 * Transform two concurrent operations against each other (TP1).
 *
 * Given operations A and B, both applied to the same document state,
 * returns [A', B'] where:
 *   apply(apply(doc, A), B') === apply(apply(doc, B), A')
 *
 * @param a - First operation
 * @param b - Second operation
 * @returns [a', b'] — transformed operations
 */
export function transform(a: OTComponents, b: OTComponents): [OTComponents, OTComponents] {
  const iterA = makeIterator(a);
  const iterB = makeIterator(b);
  const aPrime: OTComponent[] = [];
  const bPrime: OTComponent[] = [];

  while (iterA.hasNext() || iterB.hasNext()) {
    const peekA = iterA.peek();
    const peekB = iterB.peek();

    // Both exhausted
    if (peekA === null && peekB === null) break;

    // Insert in A has priority (insert-insert tie-breaking: A goes first)
    if (peekA !== null && isInsert(peekA)) {
      const ins = iterA.take(Infinity);
      aPrime.push(ins);
      bPrime.push((ins as string).length); // B' must retain over A's insert
      continue;
    }

    // Insert in B
    if (peekB !== null && isInsert(peekB)) {
      const ins = iterB.take(Infinity);
      bPrime.push(ins);
      aPrime.push((ins as string).length); // A' must retain over B's insert
      continue;
    }

    // Both are non-insert: take the minimum of their remaining lengths
    if (peekA === null || peekB === null) {
      // One is exhausted — the other should also be at the end
      // This means operations had different input lengths — error
      throw new Error('Transform: operations have different input lengths');
    }

    const lenA = isRetain(peekA) ? (peekA as number) : -(peekA as number);
    const lenB = isRetain(peekB) ? (peekB as number) : -(peekB as number);
    const minLen = Math.min(lenA, lenB);

    const compA = iterA.take(minLen);
    const compB = iterB.take(minLen);

    // Retain-Retain: both retain
    if (isRetain(compA) && isRetain(compB)) {
      aPrime.push(minLen);
      bPrime.push(minLen);
    }
    // Retain-Delete: A' must delete (skip in A'), B' deletes
    else if (isRetain(compA) && isDelete(compB)) {
      bPrime.push(compB);
      // A' has nothing — the text was deleted by B, so A's retain is dropped
    }
    // Delete-Retain: B' must skip (the text A deleted)
    else if (isDelete(compA) && isRetain(compB)) {
      aPrime.push(compA);
      // B' has nothing — the text was deleted by A, so B's retain is dropped
    }
    // Delete-Delete: both delete same text — cancel out
    else if (isDelete(compA) && isDelete(compB)) {
      // Both deleted the same region — neither needs anything
    }
  }

  return [compact(aPrime), compact(bPrime)];
}

// ─── Compose ─────────────────────────────────────────────────────────

/**
 * Compose two sequential operations into one.
 * compose(a, b) returns c where apply(doc, c) === apply(apply(doc, a), b).
 *
 * Operation a produces a document, and operation b consumes that document.
 */
export function compose(a: OTComponents, b: OTComponents): OTComponents {
  const iterA = makeIterator(a);
  const iterB = makeIterator(b);
  const result: OTComponent[] = [];

  while (iterA.hasNext() || iterB.hasNext()) {
    const peekA = iterA.peek();
    const peekB = iterB.peek();

    if (peekA === null && peekB === null) break;

    // Delete in A: pass through to result (consumes input, produces nothing for B)
    if (peekA !== null && isDelete(peekA)) {
      const del = iterA.take(Infinity);
      result.push(del);
      continue;
    }

    // Insert in B: pass through to result (consumes nothing from A's output)
    if (peekB !== null && isInsert(peekB)) {
      const ins = iterB.take(Infinity);
      result.push(ins);
      continue;
    }

    if (peekA === null && peekB !== null) {
      const lenB = isRetain(peekB) ? (peekB as number) : -(peekB as number);
      const compB = iterB.take(lenB);

      if (isRetain(compB)) {
        result.push(compB);
      } else if (isDelete(compB)) {
        result.push(compB);
      }
      continue;
    }

    if (peekB === null && peekA !== null) {
      if (isRetain(peekA)) {
        result.push(iterA.take(peekA as number));
        continue;
      }

      if (isInsert(peekA)) {
        result.push(iterA.take((peekA as string).length));
        continue;
      }

      throw new Error('Compose: operations have incompatible lengths');
    }

    const currentA = peekA as OTComponent;
    const currentB = peekB as OTComponent;

    // Determine the minimum overlap length
    let lenA: number;
    if (isRetain(currentA)) lenA = currentA;
    else if (isInsert(currentA)) lenA = currentA.length;
    else lenA = -currentA;

    let lenB: number;
    if (isRetain(currentB)) lenB = currentB;
    else if (isDelete(currentB)) lenB = -currentB;
    else lenB = 0; // shouldn't happen — insert handled above

    const minLen = Math.min(lenA, lenB);

    const compA = iterA.take(minLen);
    const compB = iterB.take(minLen);

    // Retain-Retain: retain
    if (isRetain(compA) && isRetain(compB)) {
      result.push(minLen);
    }
    // Retain-Delete: delete (A retains, B deletes from that)
    else if (isRetain(compA) && isDelete(compB)) {
      result.push(compB);
    }
    // Insert-Retain: insert (A inserts, B retains that insert)
    else if (isInsert(compA) && isRetain(compB)) {
      result.push(compA);
    }
    // Insert-Delete: cancel (A inserts, B deletes what A inserted)
    else if (isInsert(compA) && isDelete(compB)) {
      // They cancel out — nothing added to result
    }
  }

  return compact(result);
}

// ─── Utility ─────────────────────────────────────────────────────────

/** Create a no-op (identity) operation for a document of given length */
export function identity(docLength: number): OTComponents {
  if (docLength === 0) return [];
  return [docLength];
}

/** Create an operation that inserts text at a given position */
export function insertAt(pos: number, text: string, docLength: number): OTComponents {
  return compact([
    ...(pos > 0 ? [pos] : []),
    text,
    ...(docLength - pos > 0 ? [docLength - pos] : []),
  ]);
}

/** Create an operation that deletes n characters at a given position */
export function deleteAt(pos: number, count: number, docLength: number): OTComponents {
  return compact([
    ...(pos > 0 ? [pos] : []),
    -count,
    ...(docLength - pos - count > 0 ? [docLength - pos - count] : []),
  ]);
}

/**
 * Transform a cursor position through an operation.
 * Returns the new cursor position after the operation is applied.
 */
export function transformCursor(cursor: number, op: OTComponents, isOwnOp: boolean): number {
  let pos = 0;
  let newCursor = cursor;

  for (const c of op) {
    if (pos >= cursor && !isInsert(c)) break;

    if (isRetain(c)) {
      pos += c;
    } else if (isInsert(c)) {
      const insertLen = c.length;
      if (pos < cursor || (pos === cursor && !isOwnOp)) {
        newCursor += insertLen;
      }
      // Don't advance pos — insert doesn't consume input
    } else if (isDelete(c)) {
      const deleteCount = -c;
      if (pos + deleteCount <= cursor) {
        newCursor -= deleteCount;
      } else {
        // Cursor is inside the deleted range — move to start of deletion
        newCursor -= (cursor - pos);
      }
      pos += deleteCount;
    }
  }

  return Math.max(0, newCursor);
}
